import { ChevronDownIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { Link, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { useAuth } from '@/auth/auth-provider';
import { BrandLogo } from '@/components/brand-logo';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import { createLocalStore, useLocalStore } from '@/lib/local-store';
import { findNavItem, visibleNav, type VisibleNavItem, type VisibleNavSection } from '../nav';
import { useRuntimeConfig } from '../runtime-config-context';
import { isRenderableAssetUrl } from '../theme/brand-theme';
import { navLinkProps } from './nav-link';

/** Sections the user folded, remembered in this browser. */
const foldedStore = createLocalStore('rgs.nav.folded', z.array(z.string()).max(50), []);

/**
 * `current` is the entry that owns the page: the longest matching path, so /analytics/players
 * highlights Players, not Overview (/analytics) as well.
 */
function NavEntry({ item, current }: { item: VisibleNavItem; current: string | undefined }) {
  const children = item.children ?? [];
  const childActive = children.some((c) => c.id === current);
  // On a sub-page the sub-item is the active one, not its parent (/configs/effective).
  const selfActive = item.id === current;
  // The user's toggle wins; until then a parent is open while you're on it or one of its pages.
  const [toggled, setToggled] = useState<boolean | null>(null);
  const open = toggled ?? (childActive || selfActive);
  const Icon = item.icon;

  const button = (
    <SidebarMenuButton asChild isActive={selfActive} tooltip={item.label}>
      <Link {...navLinkProps(item)}>
        {Icon && <Icon aria-hidden />}
        <span>{item.label}</span>
      </Link>
    </SidebarMenuButton>
  );
  if (children.length === 0) return <SidebarMenuItem>{button}</SidebarMenuItem>;

  return (
    <Collapsible asChild open={open} onOpenChange={setToggled}>
      <SidebarMenuItem>
        {button}
        <CollapsibleTrigger asChild>
          <SidebarMenuAction
            aria-label={`${open ? 'Hide' : 'Show'} ${item.label} pages`}
            className="transition-transform data-[state=open]:rotate-90"
          >
            <ChevronRightIcon />
          </SidebarMenuAction>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarMenuSub>
            {children.map((child) => (
              <SidebarMenuSubItem key={child.id}>
                <SidebarMenuSubButton asChild isActive={child.id === current}>
                  <Link {...navLinkProps(child)}>
                    <span>{child.label}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  );
}

function NavSectionGroup({
  section,
  current,
  folded,
  onToggle,
}: {
  section: VisibleNavSection;
  current: string | undefined;
  folded: boolean;
  onToggle: () => void;
}) {
  const { state, isMobile } = useSidebar();
  // Collapsed to icons, section labels are hidden, so every item must stay reachable.
  const open = (state === 'collapsed' && !isMobile) || !folded;
  return (
    <Collapsible open={open} onOpenChange={onToggle} className="group/section">
      <SidebarGroup className="py-1">
        <SidebarGroupLabel asChild>
          <CollapsibleTrigger className="w-full justify-between text-[0.7rem] font-semibold tracking-wider uppercase hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
            {section.label}
            {/* Quiet until hovered or focused; always shown on a folded section so the fold is visible. */}
            <ChevronDownIcon
              aria-hidden
              className="opacity-0 transition group-hover/section:opacity-100 group-focus-within/section:opacity-100 group-data-[state=closed]/section:-rotate-90 group-data-[state=closed]/section:opacity-100"
            />
          </CollapsibleTrigger>
        </SidebarGroupLabel>
        <CollapsibleContent>
          <SidebarGroupContent>
            <SidebarMenu>
              {section.items.map((item) => (
                <NavEntry key={item.id} item={item} current={current} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

export function AppSidebar({
  variant,
  collapsible,
}: {
  variant: 'inset' | 'floating' | 'sidebar';
  collapsible: 'icon' | 'offcanvas' | 'none';
}) {
  const { presentation } = useRuntimeConfig();
  const { can, superAdmin } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const sections = visibleNav(presentation.navigation.items, { can, superAdmin });
  const current = findNavItem(pathname)?.id;
  const { brand } = presentation;
  const [folded, setFolded] = useLocalStore(foldedStore);
  const hasLogo = isRenderableAssetUrl(brand.logoUrl);

  return (
    <Sidebar variant={variant} collapsible={collapsible}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild size="lg" tooltip={brand.productName}>
              <Link to="/" aria-label={`${brand.productName}: home`}>
                {hasLogo ? (
                  <>
                    <span className="flex min-w-0 items-center gap-2.5 group-data-[collapsible=icon]:hidden">
                      <BrandLogo brand={brand} decorative className="h-7 w-auto shrink-0" />
                      <span className="truncate text-xs font-medium text-sidebar-foreground/70">
                        {brand.productName}
                      </span>
                    </span>
                    {isRenderableAssetUrl(brand.faviconUrl) && (
                      <img
                        src={brand.faviconUrl}
                        alt=""
                        className="hidden size-8 object-contain group-data-[collapsible=icon]:block"
                      />
                    )}
                  </>
                ) : (
                  <>
                    <span className="grid size-8 shrink-0 place-items-center rounded-md bg-primary text-sm font-semibold text-primary-foreground">
                      {(brand.shortName ?? brand.productName).slice(0, 1)}
                    </span>
                    <span className="truncate font-semibold">{brand.productName}</span>
                  </>
                )}
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent className="gap-0">
        {sections.map((section) => (
          <NavSectionGroup
            key={section.id}
            section={section}
            current={current}
            folded={folded.includes(section.id)}
            onToggle={() =>
              setFolded((prev) =>
                prev.includes(section.id) ? prev.filter((id) => id !== section.id) : [...prev, section.id],
              )
            }
          />
        ))}
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
